import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useApiClient } from '@/lib/api/useApiClient';
import { useAuth } from '@/lib/auth/useAuth';
import { xhrUpload } from './lib/xhrUpload';
import type {
  CompleteSyslogInput,
  DownloadUrlResponse,
  PresignSyslogInput,
  PresignSyslogResult,
  Upload,
  UploadConfig,
  UploadListFilters,
  UploadListResponse,
} from './types';

const uploadKeys = {
  all: ['uploads'] as const,
  list: (filters: UploadListFilters) => [...uploadKeys.all, 'list', filters] as const,
  config: () => [...uploadKeys.all, 'config'] as const,
};

function buildQuery(filters: UploadListFilters): Record<string, string | number | undefined> {
  return {
    status: filters.status,
    uploadType: filters.uploadType,
    limit: filters.limit,
    offset: filters.offset,
    customerId: filters.customerId,
  };
}

export function useUploads(filters: UploadListFilters) {
  const api = useApiClient();
  const { accessToken } = useAuth();
  return useQuery({
    queryKey: uploadKeys.list(filters),
    enabled: Boolean(accessToken),
    queryFn: () =>
      api.request<UploadListResponse>('/uploads', {
        query: buildQuery(filters),
      }),
  });
}

export function useUploadConfig() {
  const api = useApiClient();
  const { accessToken } = useAuth();
  return useQuery({
    queryKey: uploadKeys.config(),
    enabled: Boolean(accessToken),
    // Config-style endpoint — values change only on a backend deploy, so we
    // can hold the response for the whole session.
    staleTime: Infinity,
    queryFn: () => api.request<UploadConfig>('/uploads/config'),
  });
}

export interface CreateRecordingInput {
  file: File;
  onProgress?: (loaded: number, total: number) => void;
  signal?: AbortSignal;
}

// Custom mutation: the upload itself goes through XMLHttpRequest because
// `fetch` still doesn't expose upload progress in browsers (despite the spec).
// React Query's mutation wrapper still gives us isPending / isError / onSuccess
// for cache invalidation; the mutationFn just bypasses the ApiClient.
export function useCreateRecording() {
  const { accessToken } = useAuth();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ file, onProgress, signal }: CreateRecordingInput): Promise<Upload> => {
      const formData = new FormData();
      formData.append('file', file, file.name);

      const baseUrl = import.meta.env.VITE_API_URL?.replace(/\/+$/, '') ?? '';
      const url = `${baseUrl}/uploads/recording`;

      const result = await xhrUpload({
        url,
        method: 'POST',
        body: formData,
        headers: accessToken ? { Authorization: `Bearer ${accessToken}` } : {},
        onProgress,
        signal,
      });

      if (result.status < 200 || result.status >= 300) {
        const parsed = safeParse(result.body) as { message?: string } | null;
        throw new Error(parsed?.message ?? `Upload failed (HTTP ${result.status})`);
      }

      const parsed = safeParse(result.body);
      if (!parsed) throw new Error('Upload succeeded but server returned an unparseable body');
      return parsed as Upload;
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: uploadKeys.all });
    },
  });
}

export function usePresignSyslog() {
  const api = useApiClient();
  return useMutation({
    mutationFn: (input: PresignSyslogInput) =>
      api.request<PresignSyslogResult>('/uploads/syslog/presign', {
        method: 'POST',
        body: input,
      }),
  });
}

export function useCompleteSyslog() {
  const api = useApiClient();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: CompleteSyslogInput) =>
      api.request<Upload>('/uploads/syslog/complete', {
        method: 'POST',
        body: input,
      }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: uploadKeys.all });
    },
  });
}

// Manual soft-delete. The backend flips the row to 'expired' and removes the
// storage object; we invalidate the uploads cache so the list reflects the
// change. Returns the updated { id, status } shape.
export function useDeleteUpload() {
  const api = useApiClient();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (uploadId: string) =>
      api.request<{ id: string; status: string }>(`/uploads/${uploadId}`, {
        method: 'DELETE',
      }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: uploadKeys.all });
    },
  });
}

// Imperative — called from a click handler, not on render. Returns the
// presigned GET URL the browser should navigate to (or pass to an <a download>).
export interface DownloadUrlClient {
  fetchUrl: (uploadId: string) => Promise<DownloadUrlResponse>;
}

export function useDownloadUrl(): DownloadUrlClient {
  const api = useApiClient();
  return {
    fetchUrl: (uploadId: string) =>
      api.request<DownloadUrlResponse>(`/uploads/${uploadId}/download`),
  };
}

function safeParse(text: string): unknown {
  try {
    return JSON.parse(text);
  } catch {
    return null;
  }
}
